// context/ProductContext.jsx
import React, { createContext, useState, useEffect, useContext, useCallback } from 'react';

const ProductContext = createContext();

export const useProducts = () => {
    const context = useContext(ProductContext);
    if (!context) {
        throw new Error('useProducts must be used within a ProductProvider');
    }
    return context;
};

const API_URL = process.env.NODE_ENV === 'production'
    ? 'https://uswellness.co.ke/api'
    : 'http://localhost:5000/api';

// Only used if the API is completely unreachable (e.g. offline) AND
// there's nothing cached in localStorage yet from a previous
// successful fetch. Real visitors and Google's crawler will always
// hit the real API first.
const emergencyFallbackProducts = [
    {
        id: 1,
        title: "Welcome to US Wellness Kenya",
        description: "Products are loading. Please refresh in a moment.",
        short_description: "Products are loading",
        price: null,
        discount_price: null,
        image: "/static/images/products/uswellness.jpg",
        category: "Wellness",
        rating: 4.5,
        reviews: 0,
        is_new: false,
        is_bestseller: false,
        is_on_sale: false,
        stock: 0,
        brand: ""
    }
];

export const ProductProvider = ({ children }) => {
    const [products, setProducts] = useState([]);
    const [loading, setLoading] = useState(true);

    const loadProducts = useCallback(async () => {
        setLoading(true);
        try {
            const response = await fetch(`${API_URL}/products`);
            if (!response.ok) {
                throw new Error(`API returned ${response.status}`);
            }
            const data = await response.json();

            // Cache locally so a later offline visit (or a fetch
            // hiccup) still has something reasonable to show, but the
            // API response is always the source of truth when reachable.
            localStorage.setItem('products', JSON.stringify(data));
            setProducts(data);
        } catch (error) {
            console.error('❌ Failed to fetch products from API:', error);

            // Fall back to whatever was last successfully cached
            try {
                const cached = localStorage.getItem('products');
                if (cached) {
                    const parsed = JSON.parse(cached);
                    if (parsed && parsed.length > 0) {
                        setProducts(parsed);
                        setLoading(false);
                        return;
                    }
                }
            } catch (parseError) {
                console.error('❌ Failed to parse cached products:', parseError);
            }

            setProducts(emergencyFallbackProducts);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        loadProducts();
    }, [loadProducts]);

    const addProduct = useCallback(async (product) => {
        try {
            const response = await fetch(`${API_URL}/products`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(product)
            });
            if (!response.ok) {
                const errorData = await response.json().catch(() => ({}));
                throw new Error(errorData.error || `API returned ${response.status}`);
            }
            await loadProducts(); // refresh from the source of truth
        } catch (error) {
            console.error('❌ Failed to add product:', error);
            alert(`Failed to add product: ${error.message}`);
        }
    }, [loadProducts]);

    const updateProduct = useCallback(async (id, updatedData) => {
        try {
            const response = await fetch(`${API_URL}/products/${id}`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(updatedData)
            });
            if (!response.ok) {
                const errorData = await response.json().catch(() => ({}));
                throw new Error(errorData.error || `API returned ${response.status}`);
            }
            await loadProducts();
        } catch (error) {
            console.error('❌ Failed to update product:', error);
            alert(`Failed to update product: ${error.message}`);
        }
    }, [loadProducts]);

    const deleteProduct = useCallback(async (id) => {
        if (!window.confirm('Are you sure you want to delete this product?')) {
            return;
        }
        try {
            const response = await fetch(`${API_URL}/products/${id}`, {
                method: 'DELETE'
            });
            if (!response.ok) {
                const errorData = await response.json().catch(() => ({}));
                throw new Error(errorData.error || `API returned ${response.status}`);
            }
            await loadProducts();
        } catch (error) {
            console.error('❌ Failed to delete product:', error);
            alert(`Failed to delete product: ${error.message}`);
        }
    }, [loadProducts]);

    const getProduct = useCallback((id) => {
        return products.find(p => p.id === id || p.slug === id || String(p.id) === String(id));
    }, [products]);

    const refreshProducts = useCallback(() => {
        loadProducts();
    }, [loadProducts]);

    const value = {
        products,
        loading,
        addProduct,
        updateProduct,
        deleteProduct,
        getProduct,
        refreshProducts,
    };

    return (
        <ProductContext.Provider value={value}>
            {children}
        </ProductContext.Provider>
    );
};

export default ProductProvider;